import { buildClassName } from './buildClassName';
import { formatOptionPrice } from './formatOptionPrice';
import type { WheelOption } from '../types';

const PREVIEW_SIZE = 64;
const PREVIEW_CENTER = PREVIEW_SIZE / 2;
const TIRE_RADIUS = 30;
const RIM_RADIUS = 21;
const HUB_RADIUS = 4.5;

// 스포크 수는 GLB 에셋에 baked되어 있어 옵션으로 바뀌지 않습니다.
// 프리뷰도 실제 렌더링과 일치하도록 고정값을 씁니다.
const SPOKE_COUNT = 5;

function createSpokeAngles(): number[] {
  return Array.from({ length: SPOKE_COUNT }, (_, index) => (index / SPOKE_COUNT) * Math.PI * 2 - Math.PI / 2);
}

/**
 * 옵션이 바꾸는 것은 림 색상과 마감이므로, 프리뷰도 그 두 가지만 표현합니다.
 * roughness가 낮을수록(유광) 하이라이트를 강하게 얹습니다.
 */
function RimPreview({ option }: { option: WheelOption }) {
  const gradientId = `rim-sheen-${option.id}`;
  const sheenOpacity = Math.max(0.08, Math.min(0.85, 1 - option.rimRoughness));
  const spokeAngles = createSpokeAngles();

  return (
    <svg className="wheel-card__preview" viewBox={`0 0 ${PREVIEW_SIZE} ${PREVIEW_SIZE}`} aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={gradientId} cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={sheenOpacity} />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx={PREVIEW_CENTER} cy={PREVIEW_CENTER} r={TIRE_RADIUS} fill="#15161a" />
      <circle cx={PREVIEW_CENTER} cy={PREVIEW_CENTER} r={RIM_RADIUS} fill="#0a0a0c" stroke={option.rimColor} strokeWidth="2.5" />

      {spokeAngles.map((angle) => (
        <line
          key={angle}
          x1={PREVIEW_CENTER + Math.cos(angle) * HUB_RADIUS}
          y1={PREVIEW_CENTER + Math.sin(angle) * HUB_RADIUS}
          x2={PREVIEW_CENTER + Math.cos(angle) * (RIM_RADIUS - 1.5)}
          y2={PREVIEW_CENTER + Math.sin(angle) * (RIM_RADIUS - 1.5)}
          stroke={option.rimColor}
          strokeWidth="4"
          strokeLinecap="round"
        />
      ))}

      <circle cx={PREVIEW_CENTER} cy={PREVIEW_CENTER} r={HUB_RADIUS} fill={option.rimColor} />
      <circle cx={PREVIEW_CENTER} cy={PREVIEW_CENTER} r={RIM_RADIUS} fill={`url(#${gradientId})`} />
    </svg>
  );
}

export interface WheelOptionGroupProps {
  options: WheelOption[];
  selectedOptionId: string;
  onOptionSelect: (optionId: string) => void;
  /** 이동 모드에서 차량 사양을 잠글 때 true. */
  disabled?: boolean;
}

export function WheelOptionGroup({
  options,
  selectedOptionId,
  onOptionSelect,
  disabled = false,
}: WheelOptionGroupProps) {
  return (
    <ul className="wheel-card-list" aria-label="휠 선택">
      {options.map((option) => {
        const isSelected = option.id === selectedOptionId;
        return (
          <li key={option.id}>
            <button
              type="button"
              className={buildClassName('wheel-card', 'wheel-card--selected', isSelected)}
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => onOptionSelect(option.id)}
            >
              <RimPreview option={option} />
              <span className="wheel-card__body">
                <span className="wheel-card__name">{option.name}</span>
                <span className="wheel-card__meta">{`${option.finishLabel} · ${formatOptionPrice(option.price)}`}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
